import { gql, useQuery } from '@apollo/client'
import { useState } from 'react'

import {
  EventCollectionDetailForGuestQuery,
  EventCollectionDetailForGuestQueryVariables,
  EventCollectionDetailQuery,
  EventCollectionDetailQueryVariables,
  EventCollectionItemsConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { Share } from '@/core/native'
import { useAnalytics } from '@/lib/analytics'
import { useCreateShortLink } from '@/lib/url/shortLink'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation()
  const { user } = useCurrentUser()
  const { router } = useRouter<{ collectionId: string; eventId: string }>()
  const { analytics } = useAnalytics()
  const [refetching, setRefetching] = useState(false)
  const { data, loading, fetchMore, refetch } = useQuery<
    EventCollectionDetailQuery,
    EventCollectionDetailQueryVariables
  >(QUERY, {
    skip: !user,
    returnPartialData: true,
    variables: {
      collectionId: router.query.collectionId,
      collectionItemsFirstLabel: true,
      publishedOnly: true,
      pageInput: {
        limit: 12,
      },
    },
  })

  const {
    data: dataForGuest,
    loading: loadingForGuest,
    fetchMore: fetchMoreForGuest,
    refetch: refetchForGuest,
  } = useQuery<
    EventCollectionDetailForGuestQuery,
    EventCollectionDetailForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    skip: !!user,
    variables: {
      collectionId: router.query.collectionId,
      collectionItemsFirstLabel: true,
      pageInput: {
        limit: 12,
      },
    },
  })

  const getShortLinkUrl = useCreateShortLink({
    path: `/events/${data?.collection?.artistId}/collections/${router.query.collectionId}`,
  })

  return {
    state: {
      collection: data?.collection ?? dataForGuest?.collection,
      eventId: router.query.eventId,
      loading: loading || loadingForGuest || refetching,
      hasNextPage:
        !!data?.collection?.collectionItems?.pageInfo.hasNextPage ||
        !!dataForGuest?.collection?.collectionItems?.pageInfo.hasNextPage,
      userCartCount: data?.userCartCount ?? 0,
    },
    actions: {
      async onChangeLabel(isFirstLabel, name) {
        if (!user) {
          await refetchForGuest({
            collectionId: router.query.collectionId,
            collectionItemsLabel: isFirstLabel ? undefined : name,
            collectionItemsFirstLabel: isFirstLabel,
          })
          return
        }

        await refetch({
          collectionId: router.query.collectionId,
          collectionItemsLabel: isFirstLabel ? undefined : name,
          collectionItemsFirstLabel: isFirstLabel,
        })
      },
      async onFetchMore(isFirstLabel, name) {
        const currentData = data ?? dataForGuest
        if (!currentData?.collection?.collectionItems.pageInfo.hasNextPage)
          return

        setRefetching(true)
        if (!user) {
          await fetchMoreForGuest({
            variables: {
              collectionItemsLabel: isFirstLabel ? undefined : name,
              collectionItemsFirstLabel: isFirstLabel,
              pageInput: {
                cursor:
                  currentData?.collection?.collectionItems.pageInfo.endCursor,
                limit: 12,
              },
            },
          })
          setRefetching(false)
          return
        }

        await fetchMore({
          variables: {
            collectionItemsLabel: isFirstLabel ? undefined : name,
            collectionItemsFirstLabel: isFirstLabel,
            pageInput: {
              cursor:
                currentData?.collection?.collectionItems.pageInfo.endCursor,
              limit: 12,
            },
          },
        })
        setRefetching(false)
      },
      async onClickShare() {
        if (!data?.collection) return

        const { title, description, thumbnail } = data.collection
        analytics?.track('click_share', {
          applicationKey: config.applicationKey,
          artistId: data?.collection?.artistId,
          collectionId: router.query.collectionId,
          name: 'eventCollectionDetail',
        })
        const url = await getShortLinkUrl()
        await Share.share({
          title: `${title} | ${t('metaTitle')}`,
          text: description ?? '',
          url,
          dialogTitle: `${title} | ${t('metaTitle')}`,
        }).then(() => {
          analytics?.track('click_shareMethod', {
            applicationKey: config.applicationKey,
            artistId: data?.collection?.artistId,
            collectionId: router.query.collectionId,
            name: 'eventCollectionDetail',
          })
        })
      },
    },
  }
})

const QUERY = gql`
  query EventCollectionDetail(
    $collectionId: ID!
    $collectionItemsLabel: String
    $collectionItemsFirstLabel: Boolean
    $pageInput: PageInput
    $publishedOnly: Boolean
  ) {
    userCartCount
    collection(id: $collectionId) {
      id
      title
      artistId
      isEventPickup
      artist {
        id
        displayName
      }
      thumbnail {
        uri
        mediumUri
      }
      labels {
        id
        name
      }
      itemCount
      description
      collectionItems(
        label: $collectionItemsLabel
        firstLabel: $collectionItemsFirstLabel
        publishedOnly: $publishedOnly
        pageInput: $pageInput
      ) {
        ...EventCollectionItemsConnection
      }
    }
  }
  ${EventCollectionItemsConnectionFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query EventCollectionDetailForGuest(
    $collectionId: ID!
    $collectionItemsLabel: String
    $collectionItemsFirstLabel: Boolean
    $pageInput: PageInput
    $publishedOnly: Boolean
  ) {
    collection(id: $collectionId) {
      id
      title
      artistId
      isEventPickup
      artist {
        id
        displayName
      }
      thumbnail {
        uri
        mediumUri
      }
      itemCount
      description
      labels {
        name
        id
      }
      collectionItems(
        label: $collectionItemsLabel
        firstLabel: $collectionItemsFirstLabel
        publishedOnly: $publishedOnly
        pageInput: $pageInput
      ) {
        ...EventCollectionItemsConnection
      }
    }
  }
  ${EventCollectionItemsConnectionFragmentDoc}
`
